<!--
WatermarkOverlay.vue

Organizer EMS 登入後介面的編譯階段浮水印保護層

@author: wyan@yoursaas.com
@date: 2026-07-27
@ai-prompt: 依 add-organizer-watermark-protection OpenSpec 建立正常隱藏、列印與受控截圖時顯示，且支援深淺色與 OCR 辨識的重複浮水印。
-->

<script setup>
import {
	computed,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from 'vue';
import i18next from '@/i18n';

const props = defineProps({
	theme: {
		type: String,
		default: 'light',
	},
	traceCode: {
		type: String,
		default: '',
	},
});

const TILE_WIDTH = 640;
const TILE_HEIGHT = 300;
const MAX_DEVICE_PIXEL_RATIO = 2;
const WATERMARK_CAPTURE_EVENT = 'yss:watermark-capture';
const WATERMARK_PRINT_PREFERENCE_EVENT =
	'yss:watermark-print-preference';
const TRACE_CODE_PATTERN =
	/^YS1-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/;
const tileImageUrl = ref('');
const tileColumns = ref(1);
const tileCount = ref(1);
const captureActive = ref(false);
const printActive = ref(false);
const printWatermarkEnabled = ref(true);
const isActive = computed(
	() => captureActive.value || printActive.value
);
const tileIndexes = computed(() =>
	Array.from({ length: tileCount.value }, (_, index) => index)
);
const tileGridStyle = computed(() => ({
	gridTemplateColumns: `repeat(${tileColumns.value}, ${TILE_WIDTH}px)`,
	gridAutoRows: `${TILE_HEIGHT}px`,
}));
let resizeFrame = null;

const isDarkTheme = () =>
	props.theme === 'dark' ||
	document.documentElement.getAttribute('data-bs-theme') === 'dark';

const resolveInkColor = () => {
	const semanticColor = window
		.getComputedStyle(document.documentElement)
		.getPropertyValue('--bs-body-color')
		.trim();

	if (semanticColor) {
		return semanticColor;
	}

	return isDarkTheme() ? '#ffffff' : '#172033';
};

const wrapText = (context, text, maxWidth) => {
	const words = `${text || ''}`.trim().split(/\s+/);
	const lines = [];
	let currentLine = '';

	words.forEach(word => {
		const candidate = currentLine ? `${currentLine} ${word}` : word;
		if (
			currentLine &&
			context.measureText(candidate).width > maxWidth
		) {
			lines.push(currentLine);
			currentLine = word;
			return;
		}
		currentLine = candidate;
	});

	if (currentLine) {
		lines.push(currentLine);
	}

	return lines;
};

const resolveCopyrightLine = () => {
	const normalizedTraceCode = `${props.traceCode || ''}`
		.trim()
		.toUpperCase();
	if (!TRACE_CODE_PATTERN.test(normalizedTraceCode)) {
		return i18next.t('watermark.protection.copyright');
	}
	return i18next.t('watermark.protection.copyrightTrace', {
		traceCode: normalizedTraceCode,
	});
};

const renderWatermark = () => {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	if (!context) {
		tileImageUrl.value = '';
		return;
	}

	const pixelRatio = Math.min(
		window.devicePixelRatio || 1,
		MAX_DEVICE_PIXEL_RATIO
	);
	canvas.width = TILE_WIDTH * pixelRatio;
	canvas.height = TILE_HEIGHT * pixelRatio;
	context.scale(pixelRatio, pixelRatio);
	context.fillStyle = resolveInkColor();
	context.textAlign = 'center';
	context.textBaseline = 'middle';

	const darkTheme = isDarkTheme();

	context.save();
	context.translate(TILE_WIDTH / 2, 98);
	context.rotate((-18 * Math.PI) / 180);
	context.globalAlpha = darkTheme ? 0.16 : 0.1;
	context.font =
		'700 31px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
	context.fillText(
		i18next.t('watermark.protection.brand'),
		0,
		-14
	);
	context.globalAlpha = darkTheme ? 0.18 : 0.115;
	context.font =
		'700 12px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
	context.fillText(
		i18next.t('watermark.protection.proprietary'),
		0,
		24
	);
	context.restore();

	context.globalAlpha = darkTheme ? 0.24 : 0.17;
	context.font =
		'700 13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
	context.fillText(
		resolveCopyrightLine(),
		TILE_WIDTH / 2,
		168
	);

	context.globalAlpha = darkTheme ? 0.2 : 0.17;
	context.font =
		'600 11.5px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
	const noticeLines = wrapText(
		context,
		i18next.t('watermark.protection.agentNotice'),
		TILE_WIDTH - 88
	);
	let noticeY = 207;
	noticeLines.forEach(line => {
		context.fillText(line, TILE_WIDTH / 2, noticeY);
		noticeY += 16;
	});

	context.globalAlpha = darkTheme ? 0.22 : 0.18;
	context.font =
		'700 11.5px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
	context.fillText(
		i18next.t('watermark.protection.authorization'),
		TILE_WIDTH / 2,
		noticeY + 3
	);

	tileImageUrl.value = canvas.toDataURL('image/png');
};

const updateTileGrid = () => {
	const viewportWidth = Math.max(
		window.innerWidth || 0,
		document.documentElement.clientWidth || 0
	);
	const viewportHeight = Math.max(
		window.innerHeight || 0,
		document.documentElement.clientHeight || 0
	);
	const columns = Math.ceil(viewportWidth / TILE_WIDTH) + 2;
	const rows = Math.ceil(viewportHeight / TILE_HEIGHT) + 2;
	tileColumns.value = columns;
	tileCount.value = columns * rows;
};

const queueRender = () => {
	if (resizeFrame !== null) {
		window.cancelAnimationFrame(resizeFrame);
	}
	resizeFrame = window.requestAnimationFrame(() => {
		resizeFrame = null;
		updateTileGrid();
		renderWatermark();
	});
};

const handleCaptureMode = event => {
	captureActive.value = event?.detail?.active === true;
	if (captureActive.value) {
		queueRender();
	}
};

const handlePrintPreference = event => {
	printWatermarkEnabled.value = event?.detail?.enabled !== false;
};

const handleBeforePrint = () => {
	printActive.value = printWatermarkEnabled.value;
	if (printActive.value) {
		updateTileGrid();
		renderWatermark();
	}
};

const handleAfterPrint = () => {
	printActive.value = false;
	printWatermarkEnabled.value = true;
};

watch(
	() => [props.theme, props.traceCode],
	() => queueRender()
);

onMounted(() => {
	updateTileGrid();
	renderWatermark();
	window.addEventListener('resize', queueRender);
	window.addEventListener(
		WATERMARK_CAPTURE_EVENT,
		handleCaptureMode
	);
	window.addEventListener(
		WATERMARK_PRINT_PREFERENCE_EVENT,
		handlePrintPreference
	);
	window.addEventListener('beforeprint', handleBeforePrint);
	window.addEventListener('afterprint', handleAfterPrint);
	i18next.on('languageChanged', queueRender);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', queueRender);
	window.removeEventListener(
		WATERMARK_CAPTURE_EVENT,
		handleCaptureMode
	);
	window.removeEventListener(
		WATERMARK_PRINT_PREFERENCE_EVENT,
		handlePrintPreference
	);
	window.removeEventListener('beforeprint', handleBeforePrint);
	window.removeEventListener('afterprint', handleAfterPrint);
	i18next.off('languageChanged', queueRender);
	if (resizeFrame !== null) {
		window.cancelAnimationFrame(resizeFrame);
	}
});
</script>

<template>
	<div
		class="watermark-protection"
		:class="{
			'watermark-protection--active': isActive,
			'watermark-protection--print-suppressed':
				!printWatermarkEnabled,
		}"
		data-watermark-protection
		:data-watermark-active="isActive ? 'true' : 'false'"
		aria-hidden="true"
	>
		<div
			v-if="tileImageUrl"
			class="watermark-protection__tiles"
			:style="tileGridStyle"
		>
			<img
				v-for="tileIndex in tileIndexes"
				:key="tileIndex"
				class="watermark-protection__tile"
				:src="tileImageUrl"
				:width="TILE_WIDTH"
				:height="TILE_HEIGHT"
				alt=""
				draggable="false"
			/>
		</div>
	</div>
</template>

<style scoped>
.watermark-protection {
	position: fixed;
	z-index: 2147483646;
	inset: 0;
	width: 100vw;
	height: 100vh;
	pointer-events: none;
	user-select: none;
	visibility: hidden;
	opacity: 0;
	overflow: hidden;
	contain: strict;
}

.watermark-protection--active {
	visibility: visible;
	opacity: 1;
}

.watermark-protection__tiles {
	position: absolute;
	top: -150px;
	left: -320px;
	display: grid;
	width: max-content;
	height: max-content;
}

.watermark-protection__tile {
	display: block;
	width: 640px;
	height: 300px;
	max-width: none;
	pointer-events: none;
	user-select: none;
}

@media print {
	.watermark-protection {
		visibility: visible !important;
		opacity: 1 !important;
		-webkit-print-color-adjust: exact;
		print-color-adjust: exact;
	}

	.watermark-protection--print-suppressed {
		visibility: hidden !important;
		opacity: 0 !important;
	}
}
</style>
